import type { AgentConnection, Job, JobState } from '@cef-ai/vault-sdk';
import type { AgentCard } from '@/src/data/agent-cards';
import { displayAgentName } from '@/lib/vault/agent-display';

export interface ActivityRow {
  agentId: string;
  name: string;
  iconUrl?: string;
  state: JobState;
  updatedAt: string;
  /** Run wall-time, createdAt→updatedAt, in ms (0 if not computable). */
  durationMs: number;
}

/** Sort key for a job: prefer updatedAt, else createdAt. */
function jobTime(job: Job): string {
  return job.updatedAt ?? job.createdAt;
}

/** createdAt→updatedAt span in ms, clamped ≥0; 0 if either timestamp is bad. */
function jobDurationMs(job: Job): number {
  const start = Date.parse(job.createdAt);
  const end = Date.parse(job.updatedAt ?? job.createdAt);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  return Math.max(0, end - start);
}

/** Compact run duration ('' below 1s, then '4s' / '3m' / '1h 2m'). */
export function formatDuration(ms: number): string {
  if (ms < 1000) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/**
 * One row per agent — the agent's single most-recent job — sorted by recency
 * desc and sliced to `limit`. A busy agent must not fill the feed with dupes.
 */
export function buildActivityFeed(
  jobs: Job[],
  agentCards: Record<string, AgentCard>,
  agents: AgentConnection[],
  limit = 3,
): ActivityRow[] {
  const known = new Set(agents.map((a) => a.agentId));
  const latest = new Map<string, Job>();
  for (const job of jobs) {
    const id = job.agentId;
    if (!id || !known.has(id)) continue;
    const prev = latest.get(id);
    if (!prev || jobTime(job) > jobTime(prev)) latest.set(id, job);
  }
  return [...latest.values()]
    .sort((a, b) => (jobTime(a) < jobTime(b) ? 1 : -1))
    .slice(0, limit)
    .map((job) => {
      const id = job.agentId!;
      const card = agentCards[id];
      const row: ActivityRow = {
        agentId: id,
        name: card?.name ?? displayAgentName(id),
        state: job.state,
        updatedAt: jobTime(job),
        durationMs: jobDurationMs(job),
      };
      if (card?.iconUrl) row.iconUrl = card.iconUrl;
      return row;
    });
}
